/**
 * Cart Page
 *
 * Displays cart items with totals and a checkout button.
 * Mounts FunnelExperience with pageType='cart' for cart-level funnel actions
 * (free gift popup, exit intent, etc.).
 */

'use client';

import styled from '@emotion/styled';
import { useCartContext } from '@/lib/context/cart-context';
import { useCart } from '@/lib/hooks/use-cart';
import { FunnelExperience, FunnelErrorBoundary } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import { useIntl } from '@/lib/intl';
import { useFreeShipping } from '@/lib/hooks/use-free-shipping';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CartItemEntity } from '@/domain/entities/cart.entity';
import { FreeShippingProgressBar } from '@/components/organisms/SideCart/CartSummaryV2/FreeShippingProgressBar';

const PageContainer = styled.main`
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 32px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CartItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.p`
  font-weight: 600;
  margin: 0 0 4px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ItemPrice = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ItemQuantity = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Summary = styled.div`
  margin-top: 32px;
  padding: 24px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TotalRow = styled(SummaryRow)`
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 0;
  padding-top: 12px;
  border-top: 2px solid ${({ theme }) => theme.colors.border.primary};
`;

const CheckoutButton = styled.button`
  width: 100%;
  margin-top: 24px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const ContinueLink = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 12px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  border: none;
  cursor: pointer;
  text-decoration: underline;
`;

export function CartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cartToken, clearCart } = useCartContext();
  const { data: cart } = useCart(cartToken, clearCart);
  const handleFunnelAddToCart = useFunnelAddToCart();
  const { countryConfig } = useIntl();
  const urlParameters = Object.fromEntries(searchParams.entries());
  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  const items: CartItemEntity[] = cart?.items ?? [];
  // price.display is already the line total (unit_price * quantity from cart API)
  const subtotal = items.reduce(
    (sum: number, item: CartItemEntity) => sum + item.price.display,
    0,
  );

  // Derive cart context fields for funnel hook evaluation
  const cartProductIds = items.map((item) => item.product_id);
  const cartTotal = subtotal; // cents — backend expects int64 cents
  const cartItemCount = items.reduce(
    (sum: number, item: CartItemEntity) => sum + item.quantity,
    0,
  );
  const freeShipping = useFreeShipping(subtotal, priceOptions.currency);

  return (
    <PageContainer>
      <FunnelErrorBoundary>
        <FunnelExperience
          pageType="cart"
          context={{
            url: '/cart',
            cartToken: cartToken ?? undefined,
            cartProductIds,
            cartTotal,
            cartItemCount,
            country: countryConfig.countryCode,
            urlParameters,
          }}
          onAddToCart={handleFunnelAddToCart}
        />
      </FunnelErrorBoundary>

      <Title>Your Cart</Title>

      {items.length === 0 ? (
        <EmptyState>
          <p>Your cart is empty.</p>
          <ContinueLink onClick={() => navigate('/products')}>
            Continue Shopping
          </ContinueLink>
        </EmptyState>
      ) : (
        <>
          {items.map((item) => (
            <CartItemRow key={item.id}>
              <ItemImage
                src={item.image_url || '/placeholder-product.png'}
                alt={item.name}
              />
              <ItemDetails>
                <ItemName>{item.name}</ItemName>
                <ItemPrice>${(item.price.display / item.quantity / 100).toFixed(2)}</ItemPrice>
              </ItemDetails>
              <ItemQuantity>Qty: {item.quantity}</ItemQuantity>
            </CartItemRow>
          ))}

          <Summary>
            <FreeShippingProgressBar freeShipping={freeShipping} priceOptions={priceOptions} />
            <SummaryRow>
              <span>Subtotal ({items.length} items)</span>
              <span>${(subtotal / 100).toFixed(2)}</span>
            </SummaryRow>
            <SummaryRow>
              <span>Shipping</span>
              <span>FREE</span>
            </SummaryRow>
            <TotalRow>
              <span>Total</span>
              <span>${(subtotal / 100).toFixed(2)}</span>
            </TotalRow>
          </Summary>

          <CheckoutButton onClick={() => navigate(cartToken ? `/checkout/${cartToken}` : '/checkout')}>
            Proceed to Checkout
          </CheckoutButton>
          <ContinueLink onClick={() => navigate('/products')}>
            Continue Shopping
          </ContinueLink>
        </>
      )}
    </PageContainer>
  );
}
